/* Native scrolling carousel with seamless end clones. COD buttons belong solely to the app. */
(() => {
  const rootsIn = (scope, selector) => [ ...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector) ];
  const initProduct = root => {
    root._sxCleanup?.();
    const controller = new AbortController();
    const on = (node, event, handler, options = {}) => node?.addEventListener(event, handler, { ...options, signal: controller.signal });
    const track = root.querySelector('[data-sx-track]');
    const slides = track ? [...track.querySelectorAll('[data-sx-slide]:not([data-sx-clone])')] : [];
    const thumbs = [...root.querySelectorAll('[data-sx-thumb]')];
    const count = slides.length;
    const loop = count > 1;
    let active = Math.max(0, slides.findIndex(slide => slide.dataset.sxInitial === 'true'));
    let settleTimer, frame, touching = false, interacting = false, observer;
    const clones = [];
    const mod = index => ((index % count) + count) % count;
    if (track && loop) {
      const cloneSlide = slide => {
        const clone = slide.cloneNode(true);
        clone.dataset.sxClone = 'true';
        clone.removeAttribute('id');
        clone.removeAttribute('data-sx-initial');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        clone.querySelectorAll('img').forEach(img => { img.loading = 'eager'; img.removeAttribute('fetchpriority'); });
        clones.push(clone);
        return clone;
      };
      track.prepend(cloneSlide(slides[count - 1]));
      track.append(cloneSlide(slides[0]));
    }
    const markActive = index => {
      active = index;
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
      slides.forEach((slide, i) => {
        if (i !== index) slide.querySelectorAll('video').forEach(video => video.pause());
      });
    };
    const align = () => {
      if (track && count) track.scrollTo({ left: (active + (loop ? 1 : 0)) * track.clientWidth, behavior: 'instant' });
    };
    const settle = () => {
      if (!track || !count || touching) return;
      if (!interacting) { align(); return; }
      const physical = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      markActive(loop ? mod(physical - 1) : Math.min(count - 1, physical));
      if (loop && (physical === 0 || physical === count + 1)) align();
      interacting = false;
    };
    const scheduleSettle = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 140);
    };
    const goTo = index => {
      if (!track || !count) return;
      interacting = true;
      const destination = loop ? mod(index) : index;
      let physical = destination + (loop ? 1 : 0);
      // Cross the boundary through the matching clone instead of scrolling back through every photo.
      if (loop && active === count - 1 && index === count) physical = count + 1;
      if (loop && active === 0 && index === -1) physical = 0;
      track.scrollTo({ left: physical * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      scheduleSettle();
    };
    if (track && count) {
      const initial = active;
      markActive(initial);
      align();
      frame = requestAnimationFrame(() => { markActive(initial); align(); });
      on(track, 'scroll', () => {
        // A viewport change can re-snap to an end clone. Keep the chosen photo unless the visitor is navigating.
        if (!interacting) { align(); return; }
        const physical = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        markActive(loop ? mod(physical - 1) : Math.min(count - 1, physical));
        scheduleSettle();
      }, { passive: true });
      on(track, 'scrollend', settle);
      on(track, 'wheel', event => { if (event.deltaX || event.shiftKey) interacting = true; }, { passive: true });
      on(track, 'touchstart', () => { touching = true; interacting = true; }, { passive: true });
      on(track, 'touchend', () => { touching = false; scheduleSettle(); }, { passive: true });
      on(track, 'touchcancel', () => { touching = false; scheduleSettle(); }, { passive: true });
      on(track, 'keydown', event => {
        if (event.target !== track || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        goTo(event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : active + (event.key === 'ArrowRight' ? 1 : -1));
      });
      thumbs.forEach((thumb, index) => on(thumb, 'click', () => goTo(index)));
      observer = new ResizeObserver(align);
      observer.observe(track);
    }
    root._sxCleanup = () => {
      controller.abort();
      clearTimeout(settleTimer);
      cancelAnimationFrame(frame);
      observer?.disconnect();
      clones.forEach(clone => clone.remove());
      delete root._sxCleanup;
    };
  };
  const init = (scope = document) => rootsIn(scope, '[data-sx-product]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => rootsIn(event.target, '[data-sx-product]').forEach(root => root._sxCleanup?.()));
  document.addEventListener('shopify:block:select', event => {
    const details = event.target.closest?.('[data-sx-faq-item]') || document.querySelector(`[data-sx-faq-item="${CSS.escape(event.detail.blockId)}"]`);
    if (details) details.open = true;
  });
})();

/* Resolve each optional how-to anchor against the actual sections on this page. */
(() => {
  if (window.sxHowLinksReady) return;
  window.sxHowLinksReady = true;
  let frame;
  const updateLinks = () => {
    document.querySelectorAll('[data-sx-how-link]').forEach(link => {
      const product = link.closest('[data-sx-product]');
      const id = (link.dataset.sxHowTarget || '').trim().replace(/^#/, '');
      const target = id ? document.getElementById(id) : Array.from(document.querySelectorAll('[id^="sx-story-"]')).find(section => section.querySelector('.sx-story') && product && (product.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING));
      if (!target || target === link) {
        link.hidden = true;
        return;
      }
      link.setAttribute('href', '#' + encodeURIComponent(target.id));
      link.hidden = false;
    });
  };
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(updateLinks);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', updateLinks, { once: true });
  else updateLinks();
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => document.addEventListener(name, schedule));
})();
